import { useCurrency } from '@/lib/currency';
import { currencyLabel } from '@/lib/currency';
import { useId, useState } from 'react';
import { ActionIcon } from '@/components/ui/ActionIcon';
import { FileTextIcon } from '@/components/icons';
import { formatNumber } from '../dashboard-utils';
import type { DashboardChart } from '../types';
import { BarChart, DonutChart, LineChart } from './DashboardCharts';
import { chartViews, type ChartView } from '../chart-views';

export const DashboardChartCard = ({ chart }: { chart: DashboardChart }) => {
  useCurrency();
  const [table, setTable] = useState(false);
  const [selectedView, setSelectedView] = useState<ChartView>(chart.kind);
  const views = chartViews(chart);
  const view = views.some((option) => option.value === selectedView) ? selectedView : chart.kind;
  const id = useId();
  return (
    <section
      className={`dash-card dash-chart-card dash-chart-card--${chart.id}`}
      aria-labelledby={id}
    >
      <header className="dash-card__head">
        <div>
          <h3 id={id}>{chart.title}</h3>
          <p>{chart.description}</p>
        </div>
        <div className="dash-chart-controls">
          {views.length > 1 && (
            <div className="dash-chart-select">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                aria-hidden="true"
              >
                <path
                  d="M3 3v14h14M6 12l3-4 4 2 4-6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <select
                aria-label={`Type de graphique : ${chart.title}`}
                value={view}
                onChange={(event) => {
                  const option = views.find((item) => item.value === event.target.value);
                  if (option) {
                    setSelectedView(option.value);
                    setTable(false);
                  }
                }}
              >
                {views.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <svg
                className="dash-chart-select__chevron"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                aria-hidden="true"
              >
                <path d="m5 6 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          <ActionIcon
            label={table ? 'Afficher le graphique' : 'Afficher les données'}
            aria-pressed={table}
            onClick={() => setTable(!table)}
          >
            <FileTextIcon />
          </ActionIcon>
        </div>
      </header>
      {table ? (
        <div
          className="dash-data-table"
          role="region"
          aria-label={`Données : ${chart.title}`}
          tabIndex={0}
        >
          <table>
            <caption className="sr-only">{chart.title}</caption>
            <thead>
              <tr>
                <th scope="col">{chart.kind === 'line' ? 'Période' : 'Libellé'}</th>
                {chart.kind === 'line' || chart.kind === 'stacked' ? (
                  chart.series.map((series) => (
                    <th scope="col" key={series.id}>
                      {series.label}
                    </th>
                  ))
                ) : (
                  <>
                    <th scope="col">
                      {chart.unit === 'eur' ? currencyLabel('Moyenne (€)') : 'Nombre'}
                    </th>
                    {chart.kind === 'range' && (
                      <>
                        <th scope="col">{currencyLabel('Min. (€)')}</th>
                        <th scope="col">{currencyLabel('Max. (€)')}</th>
                        <th scope="col">Échantillon</th>
                      </>
                    )}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {chart.kind === 'line'
                ? chart.labels.map((label, index) => (
                    <tr key={index}>
                      <th scope="row">{label}</th>
                      {chart.series.map((series) => (
                        <td key={series.id}>{formatNumber(series.values[index] ?? 0)}</td>
                      ))}
                    </tr>
                  ))
                : chart.rows.map((row) => (
                    <tr key={row.id}>
                      <th scope="row">{row.label}</th>
                      {chart.kind === 'stacked' ? (
                        chart.series.map((series, i) => (
                          <td key={series.id}>{formatNumber(row.values?.[i] ?? 0)}</td>
                        ))
                      ) : (
                        <>
                          <td>{formatNumber(row.value, chart.unit)}</td>
                          {chart.kind === 'range' && (
                            <>
                              <td>{formatNumber(row.min ?? null, 'eur')}</td>
                              <td>{formatNumber(row.max ?? null, 'eur')}</td>
                              <td>{formatNumber(row.sample ?? null)}</td>
                            </>
                          )}
                        </>
                      )}
                    </tr>
                  ))}
            </tbody>
          </table>
          {!chart.rows.length && !chart.labels.length && <p>Aucune donnée disponible.</p>}
        </div>
      ) : chart.kind === 'line' ? (
        <LineChart chart={chart} variant={view === 'area' || view === 'columns' ? view : 'line'} />
      ) : view === 'donut' ? (
        <DonutChart chart={chart} />
      ) : (
        <BarChart chart={chart} />
      )}
      <footer className="dash-card__foot">
        <span>
          <i />
          {chart.kind === 'line'
            ? 'Survolez une période pour explorer'
            : chart.kind === 'range'
              ? 'Montants en euros par jour'
              : view === 'donut'
                ? 'Explorez chaque catégorie'
                : 'Classement sur la période'}
        </span>
        <span>
          {chart.kind === 'line'
            ? `${String(chart.labels.length)} périodes`
            : `${String(chart.rows.length)} catégories`}
        </span>
      </footer>
    </section>
  );
};
